import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
    BaseComponent,
    LogService,
    NotificationChannel,
    NotificationSetting,
    NotificationSettingsService
} from '@ridango/core';
import { switchMap, take, tap, takeUntil, filter, delay } from 'rxjs/operators';
import { defer, of, timer } from 'rxjs';
import Env from '@ridango/core/env';
import { ApplicationFacade, DeviceNotificationService, NotificationFacade } from '@ridango/components';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { ModalController } from '@ionic/angular';
import { PermissionState } from '@ridango/components/lib/firebase/device-notification.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { Store } from '@ngrx/store';
import { openOsNotificationSettings } from '@ridango/components/lib/permissions';
import { PermissionNotificationModalComponent } from '../permission-code-modal/permission-notification-modal.component';

@Component({
    selector: 'rwidget-legacy-notification-settings',
    templateUrl: './legacy-notification-settings.component.html',
    styleUrls: ['./legacy-notification-settings.component.scss']
})
export class LegacyNotificationSettingsComponent extends BaseComponent implements OnInit, OnDestroy {
    @ViewChild('singleToggle') singleToggle;
    notificationSettings: NotificationSetting = null;
    assetsPath$ = this.applicationFacade.assetsPath$;
    loggedIn = false;
    checkinRegistrationToken;
    removeFirebaseToken;
    localToken;
    oldToken;
    constructor(
        private notificationSettingsService: NotificationSettingsService,
        private ls: LogService,
        private pushNotification: DeviceNotificationService,
        private applicationFacade: ApplicationFacade,
        private modalCtrl: ModalController,
        private clipboard: Clipboard,
        private notification: NotificationFacade,
        private store$: Store,
        @Inject('ENV') public env: Env
    ) {
        super();
    }

    ngOnInit(): void {
        if (this.env.APP_NOTIFICATIONS?.HIDE_IN_APP_TOGGLE) return;
        this.notificationSettingsService
            .getNotificationSettings()
            .pipe(
                take(1),
                switchMap((response) => {
                    if (response && response.channels) {
                        return of(response);
                    } else {
                        const notificationSetting = new NotificationSetting();
                        notificationSetting.channels = new NotificationChannel();

                        for (const channel of this.env.NOTIFICATION_CHANNELS) {
                            notificationSetting.channels[channel] = true;
                        }

                        return this.notificationSettingsService.updateNotificationSettings(notificationSetting);
                    }
                }),
                tap((notificationSettings) => {
                    this.notificationSettings = notificationSettings;
                })
            )
            .subscribe();

        if (Capacitor.isNativePlatform() && this.env.NOTIFICATION_CHANNELS?.length === 1) {
            defer(() => PushNotifications.checkPermissions())
                .pipe(
                    take(1),
                    takeUntil(this.componentDestroyed$),
                    filter(({ receive }) => receive !== PermissionState.Granted),
                    delay(500)
                )
                .subscribe(() => this.handleOffToggle());
        }
    }

    get checkPermission(): boolean {
        return (
            this.env.NOTIFICATION_CHANNELS?.length === 1 &&
            this.notificationSettings.channels[this.env.NOTIFICATION_CHANNELS[0]] &&
            Capacitor.isNativePlatform()
        );
    }

    async updateNotificationSettings() {
        if (this.checkPermission) {
            const status = await this.checkNotificationPermission();

            if (!status) {
                return;
            }
        }

        this.notificationSettingsService
            .updateNotificationSettings(this.notificationSettings)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (notificationSettings) => {
                    this.notificationSettings = notificationSettings;
                    this.handlePushNotification();
                },
                (e) => {
                    this.ls.log(this, 'submit e', e);
                }
            );
    }

    async checkNotificationPermission(): Promise<boolean> {
        const permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === PermissionState.Denied) {
            await this.handleRequestPermissionNotification();
            this.handleOffToggle();
            return false;
        }

        if (permStatus.receive !== PermissionState.Granted) {
            const status = await PushNotifications.requestPermissions();

            if (status.receive !== PermissionState.Granted) {
                this.handleOffToggle();
                return false;
            }
        }

        return true;
    }

    handleOffToggle() {
        this.singleToggle.checked = false;
        this.notificationSettings.channels[this.env.NOTIFICATION_CHANNELS[0]] = false;
    }

    async handleRequestPermissionNotification() {
        const modal = await this.modalCtrl.create({
            component: PermissionNotificationModalComponent,
            cssClass: 'permission-notification-modal'
        });

        await modal.present();
    }

    isAnyChecked(): boolean {
        return this.env.NOTIFICATION_CHANNELS?.some((channel) => this.notificationSettings?.channels[channel]);
    }

    get showToggleSlideStyle(): boolean {
        return this.env.APP_NAME === 'sormland';
    }

    toggleAll() {
        const isAnyChecked = this.isAnyChecked();
        for (const channel of this.env.NOTIFICATION_CHANNELS) {
            this.notificationSettings.channels[channel] = !isAnyChecked;
        }

        this.updateNotificationSettings();
    }

    handlePushNotification() {
        const platform = Capacitor.getPlatform();

        if (platform === 'web') return;

        if (this.notificationSettings.channels[this.env.NOTIFICATION_CHANNELS[0]]) {
            // It takes time to re-generate a new token after unregister from the previous token (Android only)
            platform === 'android'
                ? timer(1500)
                      .pipe(take(1))
                      .subscribe(() => this.pushNotification.initialize())
                : this.pushNotification.initialize();
        } else {
            this.pushNotification.unsubscribe();
        }
    }

    handleBackClicked(): void {
        window.history.back();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    openOsNotificationSettings() {
        this.store$.dispatch(openOsNotificationSettings());
    }
}
